import React, { useState, useEffect, useLayoutEffect, useRef, MouseEvent } from 'react';
import axios from 'axios';
import './App.css';
import ReactDOM from 'react-dom';
import {
  ATLAS_FALLBACK_DESCRIPTION,
  ATLAS_FALLBACK_STATS,
  CATEGORY_LABELS,
  DDRAGON_LOCALE,
  Locale,
  STAT_DISPLAY_EN,
  SearchEntry,
  UI,
  buildSearchEntry,
  buildStatPattern,
  detectLocale,
  matchesQuery,
  pathForLocale,
  statByLabel,
} from './i18n';

const locale = detectLocale();
const t = UI[locale];

interface StatTranslation {
  [key: string]: string;
}

interface CategoryTranslation {
  [key: string]: string;
}

// interface DetailedStatTranslation {
//   [key: string]: string;
// }

interface Item {
  id: string;
  name: string;
  colloq?: string;
  gold: {
    total: number;
    purchasable: boolean;
    sell: number;
  };
  description: string;
  plaintext: string;
  tags: string[];
  from?: string[];
  into?: string[];
  maps: {
    [key: string]: boolean;
  };
}

interface OverlayProps {
  children: React.ReactNode;
  /** Viewport rect of the hovered tile; the overlay anchors itself to this. */
  anchor: DOMRect;
}

const statTranslation: StatTranslation = {
  'Health': '체력',
  'Base Health Regen': '기본 체력 재생',
  'Armor': '방어력',
  'Magic Resist': '마법 저항력',
  'Tenacity': '강인함',
  'Attack Damage': '공격력',
  'Attack Speed': '공격 속도',
  'Critical Strike Chance': '치명타 확률',
  'Lethality': '물리 관통력',
  'Armor Penetration': '방어구 관통력',
  'Life Steal': '생명력 흡수',
  'Ability Haste': '스킬 가속',
  'Mana': '마나',
  'Base Mana Regen': '기본 마나 재생',
  'Ability Power': '주문력',
  'Magic Penetration': '마법 관통력(고정 수치)',
  'Percent Magic Penetration': '마법 관통력(%)',
  'Movement Speed': '이동 속도(고정 수치)',
  'Percent Movement Speed': '이동 속도(%)',
  'Health and Shield Power': '체력 회복 및 보호막'
};

const jackOfAllTradesStats = Object.keys(statTranslation);

const statPattern = buildStatPattern(locale);

/** Display name for a canonical stat id. */
const statName = (id: string): string =>
  locale === 'ko' ? statTranslation[id] : STAT_DISPLAY_EN[id] ?? id;

const categoryTranslation: CategoryTranslation = CATEGORY_LABELS[locale];

// const detailedStatTranslation: DetailedStatTranslation = {
//   'FlatHPPoolMod': '체력',
//   'rFlatHPModPerLevel': '레벨당 체력',
//   'FlatMPPoolMod': '마나',
//   'rFlatMPModPerLevel': '레벨당 마나',
//   'PercentHPPoolMod': '체력(%)',
//   'PercentMPPoolMod': '마나(%)',
//   'FlatHPRegenMod': '체력 재생',
//   'rFlatHPRegenModPerLevel': '레벨당 체력 재생',
//   'PercentHPRegenMod': '체력 재생(%)',
//   'FlatMPRegenMod': '마나 재생',
//   'rFlatMPRegenModPerLevel': '레벨당 마나 재생',
//   'PercentMPRegenMod': '마나 재생(%)',
//   'FlatArmorMod': '방어력',
//   'rFlatArmorModPerLevel': '레벨당 방어력',
//   'PercentArmorMod': '방어력(%)',
//   'rFlatArmorPenetrationMod': '방어구 관통력',
//   'rFlatArmorPenetrationModPerLevel': '레벨당 방어구 관통력',
//   'rPercentArmorPenetrationMod': '방어구 관통력(%)',
//   'rPercentArmorPenetrationModPerLevel': '레벨당 방어구 관통력(%)',
//   'FlatPhysicalDamageMod': '공격력',
//   'rFlatPhysicalDamageModPerLevel': '레벨당 공격력',
//   'PercentPhysicalDamageMod': '공격력(%)',
//   'FlatMagicDamageMod': '주문력',
//   'rFlatMagicDamageModPerLevel': '레벨당 주문력',
//   'PercentMagicDamageMod': '주문력(%)',
//   'FlatMovementSpeedMod': '이동 속도',
//   'rFlatMovementSpeedModPerLevel': '레벨당 이동 속도',
//   'PercentMovementSpeedMod': '이동 속도(%)',
//   'rPercentMovementSpeedModPerLevel': '레벨당 동 속도(%)',
//   'FlatAttackSpeedMod': '공격 속도',
//   'PercentAttackSpeedMod': '공격 도(%)',
//   'rPercentAttackSpeedModPerLevel': '레벨당 공격 속도(%)',
//   'rFlatDodgeMod': '회피',
//   'rFlatDodgeModPerLevel': '레벨당 회피',
//   'PercentDodgeMod': '회피(%)',
//   'FlatCritChanceMod': '치명타 확률',
//   'rFlatCritChanceModPerLevel': '레벨당 치명타 확률',
//   'PercentCritChanceMod': '치명타 확률(%)',
//   'FlatCritDamageMod': '명타 피해',
//   'rFlatCritDamageModPerLevel': '레벨당 치명타 피해',
//   'PercentCritDamageMod': '명타 피해(%)',
//   'FlatBlockMod': '방어',
//   'PercentBlockMod': '방어(%)',
//   'FlatSpellBlockMod': '마법 저항력',
//   'rFlatSpellBlockModPerLevel': '레벨당 마법 저항력',
//   'PercentSpellBlockMod': '마법 저항력(%)',
//   'FlatEXPBonus': '경험치 보너스',
//   'PercentEXPBonus': '경험치 보너스(%)',
//   'rPercentCooldownMod': '재사용 대기시간 감소(%)',
//   'rPercentCooldownModPerLevel': '레벨당 재사용 대기시간 감소(%)',
//   'rFlatTimeDeadMod': '사망 시간 감소',
//   'rFlatTimeDeadModPerLevel': '레벨당 사망 시간 소',
//   'rPercentTimeDeadMod': '사망 시간 감소(%)',
//   'rPercentTimeDeadModPerLevel': '레벨당 사망 시간 감소(%)',
//   'rFlatGoldPer10Mod': '10초당 골드',
//   'rFlatMagicPenetrationMod': '마법 관통력',
//   'rFlatMagicPenetrationModPerLevel': '레벨당 마법 관통력',
//   'rPercentMagicPenetrationMod': '마법 관통(%)',
//   'rPercentMagicPenetrationModPerLevel': '레벨당 마법 관통력(%)',
//   'FlatEnergyRegenMod': '에너지 재생',
//   'rFlatEnergyRegenModPerLevel': '레벨당 에너지 재생',
//   'FlatEnergyPoolMod': '에너지',
//   'rFlatEnergyModPerLevel': '레벨당 에너지',
//   'PercentLifeStealMod': '생명력 흡수(%)',
//   'PercentSpellVampMod': '주문 흡혈(%)'
// };

// Gap between the tile and the overlay, and the minimum breathing room at the viewport edge.
const OVERLAY_GAP = 8;
const VIEWPORT_MARGIN = 10;

const Overlay: React.FC<OverlayProps> = ({ children, anchor }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  // Measure the rendered box instead of guessing its size: the overlay's height depends on the
  // item's description and its width comes from CSS, so hardcoded constants drift out of sync.
  useLayoutEffect(() => {
    const element = overlayRef.current;
    if (!element) return;

    const { width, height } = element.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    // Prefer the right of the tile, flip to the left only when it genuinely doesn't fit,
    // and fall back to pinning against the edge when neither side has room.
    let x = anchor.right + OVERLAY_GAP;
    if (x + width + VIEWPORT_MARGIN > innerWidth) {
      const flipped = anchor.left - OVERLAY_GAP - width;
      x = flipped >= VIEWPORT_MARGIN ? flipped : innerWidth - width - VIEWPORT_MARGIN;
    }

    // Top-align with the tile, then lift it back up if it would run past the bottom.
    let y = anchor.top;
    if (y + height + VIEWPORT_MARGIN > innerHeight) {
      y = innerHeight - height - VIEWPORT_MARGIN;
    }

    const next = { x: Math.max(VIEWPORT_MARGIN, x), y: Math.max(VIEWPORT_MARGIN, y) };
    setPosition((prev) => (prev && prev.x === next.x && prev.y === next.y ? prev : next));
  }, [anchor]);

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="item-details-overlay"
      // Rendered off-view until measured, so it never flashes at the wrong spot.
      style={
        position
          ? { left: `${position.x}px`, top: `${position.y}px` }
          : { left: 0, top: 0, visibility: 'hidden' }
      }
    >
      {children}
    </div>,
    document.body
  );
};

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
};

interface ItemSheetProps {
  children: React.ReactNode;
  actionLabel: string;
  actionDisabled?: boolean;
  onAction: () => void;
  onClose: () => void;
}

// Touch devices have no hover state, so item details open as a bottom sheet instead.
const ItemSheet: React.FC<ItemSheetProps> = ({ children, actionLabel, actionDisabled, onAction, onClose }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return ReactDOM.createPortal(
    <div className="item-sheet-backdrop" onClick={onClose}>
      <div
        className="item-sheet"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="item-sheet-handle" />
        <button className="item-sheet-close" onClick={onClose} aria-label={t.close}>✕</button>
        <div className="item-sheet-body">{children}</div>
        <div className="item-sheet-actions">
          <button className="item-sheet-action" onClick={onAction} disabled={actionDisabled}>
            {actionLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Define the stripHtmlTags function before it is used
const stripHtmlTags = (html: string): string => {
  // Remove Ornn icon from item names
  const withoutOrnnIcon = html.replace(/%i:ornnIcon%/g, '');

  // Replace <br> tags with a placeholder
  let withPlaceholder = withoutOrnnIcon.replace(/<br\s*\/?>/gi, '###BR###');

  // Replace <active> and <passive> tags with bold
  withPlaceholder = withPlaceholder.replace(/<(active|passive|attention)>/gi, '###bold###');
  withPlaceholder = withPlaceholder.replace(/<\/(active|passive|attention)>/gi, '###/bold###');

  // Create a temporary div element
  const div = document.createElement('div');
  div.innerHTML = withPlaceholder;

  // Get the text content
  let stripped = div.textContent || div.innerText || '';

  // Replace the placeholder back with <br> tags
  stripped = stripped.replace(/###BR###/g, '<br>');
  stripped = stripped.replace(/###bold###/g, '<b>');
  stripped = stripped.replace(/###\/bold###/g, '</b>');

  return stripped;
};

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24시간

const fetchVersionWithCache = async () => {
  const cachedData = localStorage.getItem('versions');
  const cachedTimestamp = localStorage.getItem('versionsTimestamp');

  if (cachedData && cachedTimestamp) {
    const isExpired = Date.now() - Number(cachedTimestamp) > CACHE_DURATION;
    if (!isExpired) {
      return JSON.parse(cachedData);
    }
  }

  const response = await axios.get<string[]>('https://ddragon.leagueoflegends.com/api/versions.json');
  localStorage.setItem('versions', JSON.stringify(response.data));
  localStorage.setItem('versionsTimestamp', Date.now().toString());
  return response.data;
};

// Recent patches flag ARAM-only items (수호자 시리즈, 카파 주스) with maps[11], so map 11 alone
// lets them into the Summoner's Rift list. Those patches also set maps[453] on everything
// actually purchasable on SR, which separates them — but older patches have no 453 key at all
// (13.24.1 only ships 11/12/21/22/30), so requiring it there would empty the list. Apply it
// only when the selected version's data actually provides it.
// Duplicate jungle-pet entries; the real ones are 1101-1103. Patches around 14.x-15.x still
// flag these for map 11, so they need dropping by id.
const JUNGLE_PET_DUPLICATE_IDS = new Set(['1105', '1106', '1107']);

// Mode-specific item variants (Arena, Swarm, ...) live above this id and are flagged for map 11
// on some patches.
const MODE_VARIANT_ID_FLOOR = 320000;

interface LocalizedItem {
  name: string;
  colloq?: string;
}

/**
 * Fetches the patch in both languages. The page only needs one of them to render, but Riot's
 * Korean colloq holds about six times the nicknames of the English one, so the search index is
 * built from both regardless of which language is on screen. The second request is best-effort:
 * losing it costs nicknames, not items.
 */
const fetchItemsWithAliases = async (
  targetVersion: string
): Promise<{ items: [string, Item][]; searchIndex: Map<string, SearchEntry> }> => {
  const url = (loc: Locale) =>
    `https://ddragon.leagueoflegends.com/cdn/${targetVersion}/data/${DDRAGON_LOCALE[loc]}/item.json`;
  const otherLocale: Locale = locale === 'ko' ? 'en' : 'ko';

  const [primary, other] = await Promise.all([
    axios.get<{ data: { [key: string]: Item } }>(url(locale)),
    axios
      .get<{ data: { [key: string]: LocalizedItem } }>(url(otherLocale))
      .then((response) => response.data.data)
      .catch(() => ({} as { [key: string]: LocalizedItem })),
  ]);

  const items = filterSummonersRiftItems(Object.entries(primary.data.data));
  const searchIndex = new Map(
    items.map(([id, item]) => [
      id,
      buildSearchEntry(id, [item.name, other[id]?.name], [item.colloq, other[id]?.colloq]),
    ])
  );

  return { items, searchIndex };
};

const filterSummonersRiftItems = (allItems: [string, Item][]): [string, Item][] => {
  const dataHasMap453 = allItems.some(([, item]) => item.maps && item.maps[453] !== undefined);

  return allItems.filter(([id, item]) =>
    Boolean(
      item.maps &&
        item.maps[11] &&
        (!dataHasMap453 || item.maps[453]) &&
        item.gold.purchasable &&
        item.gold.total > 0 &&
        // Every real shop item depreciates on sale. Selling back at full price marks an event
        // item (카파 주스), which Riot flags for map 11 on patches that predate the 453 key.
        item.gold.sell < item.gold.total &&
        parseInt(id, 10) < MODE_VARIANT_ID_FLOOR &&
        !JUNGLE_PET_DUPLICATE_IDS.has(id)
    )
  );
};

const App: React.FC = () => {
  const [items, setItems] = useState<[string, Item][]>([]);
  const [version, setVersion] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<(string | null)[]>(Array(6).fill(null));
  const [debug, setDebug] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<{ type: string; value: string }>({ type: '', value: '' });
  const [overlayAnchor, setOverlayAnchor] = useState<DOMRect | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const itemListRef = useRef<HTMLDivElement>(null);
  const [availableVersions, setAvailableVersions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isTouch = useMediaQuery('(hover: none)');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [searchIndex, setSearchIndex] = useState<Map<string, SearchEntry>>(new Map());
  const [sheetItem, setSheetItem] = useState<{ id: string; slotIndex: number | null } | null>(null);
  const [statsOpen, setStatsOpen] = useState<boolean>(true);
  const [jackOpen, setJackOpen] = useState<boolean>(true);

  // The summary sections push the item list far down on narrow screens, so collapse them there.
  useEffect(() => {
    setStatsOpen(!isMobile);
    setJackOpen(!isMobile);
  }, [isMobile]);

  // Filter rows scroll sideways on mobile, so pull the active chip back into view when it drifts off.
  useEffect(() => {
    if (!activeFilter.value) return;
    document
      .querySelector('.filter-button.active')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [activeFilter]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setDebug(params.get('debug') === 'true');

    const fetchVersion = async () => {
      setIsLoading(true);
      try {
        const versions = await fetchVersionWithCache();
        setAvailableVersions(versions);
        setVersion(versions[0]);

        const loaded = await fetchItemsWithAliases(versions[0]);
        setItems(loaded.items);
        setSearchIndex(loaded.searchIndex);
      } catch (error) {
        console.error('Error fetching version:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersion();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setHoveredItem(null);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleItemClick = (id: string) => {
    setSelectedItems((prevSelectedItems) => {
      const index = prevSelectedItems.findIndex(item => item === null);
      if (index !== -1) {
        const newSelectedItems = [...prevSelectedItems];
        newSelectedItems[index] = id;
        return newSelectedItems;
      }
      return prevSelectedItems;
    });
  };

  const handleSelectedItemRemove = (index: number) => {
    setSelectedItems((prevSelectedItems) => {
      // Filter out the null values and the item to be removed
      const newSelectedItems = prevSelectedItems.filter((_, i) => i !== index && prevSelectedItems[i] !== null);
      // Fill the remaining slots with null
      while (newSelectedItems.length < 6) {
        newSelectedItems.push(null);
      }
      return newSelectedItems;
    });

    // Close the overlay when an item is removed
    setHoveredItem(null);
  };

  const openItemSheet = (id: string, slotIndex: number | null) => {
    setHoveredItem(null);
    setSheetItem({ id, slotIndex });
  };

  const closeItemSheet = () => {
    setSheetItem(null);
  };

  const handleSheetAction = () => {
    if (!sheetItem) return;

    if (sheetItem.slotIndex !== null) {
      handleSelectedItemRemove(sheetItem.slotIndex);
    } else {
      handleItemClick(sheetItem.id);
    }

    closeItemSheet();
  };

  const parseDescriptionForStats = (description: string, itemId: string): { [key: string]: { value: number } } => {
    // 세계 지도집 / World Atlas ships an empty description in every locale.
    if (itemId === '3865' && description.trim() === '') {
      return { ...ATLAS_FALLBACK_STATS[locale] };
    }

    const cleanDescription = description.replace(/<\/?b>/g, '');
    const stats: { [key: string]: { value: number } } = {};

    for (const line of cleanDescription.split('<br>')) {
      const match = line.match(statPattern);
      if (!match) continue;

      // ko captures (label, value, percent); en puts the number first: (value, percent, label).
      const [label, rawValue, percentSign] =
        locale === 'ko' ? [match[1], match[2], match[3]] : [match[3], match[1], match[2]];

      const stat = statByLabel(locale, label);
      if (!stat) continue;

      const statType = statName(percentSign === '%' && stat.percent ? stat.percent : stat.flat);

      // First line wins. An item's passive text can restate a stat it already grants — 새벽심장's
      // "기본 마나 재생 100%당 ..." — and summing that counted the stat twice.
      if (stats[statType]) continue;
      stats[statType] = { value: parseInt(rawValue, 10) };
    }

    return stats;
  };

  const calculateTotalStats = (): { [key: string]: number } => {
    const totalStats: { [key: string]: number } = {};

    selectedItems.forEach(id => {
      if (id) {
        const item = items.find(([itemId]) => itemId === id);
        if (item) {
          const itemStats = parseDescriptionForStats(stripHtmlTags(item[1].description), id);
          Object.entries(itemStats).forEach(([stat, { value }]) => {
            if (totalStats[stat]) {
              totalStats[stat] += value;
            } else {
              totalStats[stat] = value;
            }
          });
        }
      }
    });

    return totalStats;
  };

  const calculateJackOfAllTrades = () => {
    const totalStats = calculateTotalStats();
    const activeStats = jackOfAllTradesStats.filter(stat => totalStats[statName(stat)]);
    const inactiveStats = jackOfAllTradesStats.filter(stat => !totalStats[statName(stat)]);

    const stackCount = activeStats.length;
    const abilityHaste = stackCount;
    const adaptiveBonus = stackCount >= 10 ? 25 : stackCount >= 5 ? 10 : 0;

    return { activeStats, inactiveStats, stackCount, abilityHaste, adaptiveBonus };
  };

  const { activeStats, inactiveStats, stackCount, abilityHaste, adaptiveBonus } = calculateJackOfAllTrades();
  const totalStats = calculateTotalStats(); // Define totalStats here

  const handleCategoryButtonClick = (tag: string) => {
    setActiveFilter((prevFilter) =>
      prevFilter.type === 'category' && prevFilter.value === tag ? { type: '', value: '' } : { type: 'category', value: tag }
    );
  };

  const handleJackOfAllTradesFilterClick = (stat: string) => {
    setActiveFilter((prevFilter) =>
      prevFilter.type === 'jack' && prevFilter.value === stat ? { type: '', value: '' } : { type: 'jack', value: stat }
    );
  };

  const filterItems = (items: [string, Item][]) => {
    if (!activeFilter.value) return items.sort((a, b) => a[1].gold.total - b[1].gold.total);

    let filteredItems;
    if (activeFilter.type === 'category') {
      filteredItems = items.filter(([id, item]) => item.tags.includes(activeFilter.value));
    } else if (activeFilter.type === 'jack') {
      filteredItems = items.filter(([id, item]) => {
        const itemStats = parseDescriptionForStats(stripHtmlTags(item.description), id);
        return itemStats[statName(activeFilter.value)];
      });
    } else {
      filteredItems = items;
    }

    return filteredItems.sort((a, b) => a[1].gold.total - b[1].gold.total);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filterItemsBySearch = (items: [string, Item][], query: string) => {
    if (!query.trim()) return items;

    return items.filter(([id, item]) => {
      const entry = searchIndex.get(id);
      return entry
        ? matchesQuery(entry, query)
        : item.name.toLowerCase().includes(query.trim().toLowerCase());
    });
  };

  const handleResetItems = () => {
    setSelectedItems(Array(6).fill(null));
  };

  const handleItemMouseEnter = (id: string, event: MouseEvent<HTMLDivElement>) => {
    // Touch devices get the bottom sheet instead of the synthesized hover overlay.
    if (isTouch) return;

    setHoveredItem(id);
    setOverlayAnchor(event.currentTarget.getBoundingClientRect());
  };

  const handleItemMouseLeave = () => {
    setHoveredItem(null);
  };

  // The bottom sheet covers the tapped tile, so it renders the icon itself; the hover overlay doesn't need to.
  const renderOverlayContent = (id: string, withIcon = false) => {
    const item = items.find(([itemId]) => itemId === id)?.[1];
    if (!item) return null;
    const description = id === '3865' && item.description.trim() === ''
      ? ATLAS_FALLBACK_DESCRIPTION[locale]
      : stripHtmlTags(item.description);
    return (
      <>
        <div className="detail-heading">
          {withIcon && (
            <img
              className="detail-icon"
              src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${id}.png`}
              alt={item.name}
            />
          )}
          <h2>{item.name}</h2>
        </div>
        {debug && <p>ID: {id}</p>}
        <p><b>{t.cost}</b> {item.gold.total} {t.goldUnit}</p>
        <p><i>{item.plaintext}</i></p>
        <p><strong>{t.effects}</strong><br />
          <span dangerouslySetInnerHTML={{ __html: description }} />
        </p>
        <div>
          <strong>{t.buildsInto}</strong>
          <div className="into-images">
            {item.into ? item.into
              .filter(intoId => {
                const intoItem = items.find(([itemId]) => itemId === intoId);
                return intoItem && intoItem[1].maps && intoItem[1].maps[11];
              })
              .map((intoId) => (
                <img
                  key={intoId}
                  src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${intoId}.png`}
                  alt={`Item ${intoId}`}
                  className="into-item"
                />
              )) : <p>{t.none}</p>}
          </div>
        </div>
        <div>
          <strong>{t.buildsFrom}</strong>
          <div className="from-images">
            {item.from ? item.from
              .filter(fromId => {
                const fromItem = items.find(([itemId]) => itemId === fromId);
                return fromItem && fromItem[1].maps && fromItem[1].maps[11];
              })
              .map((fromId) => (
                <img
                  key={fromId}
                  src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${fromId}.png`}
                  alt={`Item ${fromId}`}
                  className="from-item"
                />
              )) : <p>{t.none}</p>}
          </div>
        </div>
      </>
    );
  };

  const calculateTotalGold = (): number => {
    return selectedItems.reduce((total, id) => {
      if (id) {
        const item = items.find(([itemId]) => itemId === id);
        if (item) {
          return total + item[1].gold.total;
        }
      }
      return total;
    }, 0);
  };

  const handleVersionChange = async (selectedVersion: string) => {
    try {
      setIsLoading(true);
      setVersion(selectedVersion);

      const loaded = await fetchItemsWithAliases(selectedVersion);
      setItems(loaded.items);
      setSearchIndex(loaded.searchIndex);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="header">
        <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" className="app-icon" />
        <h1 className="header-title">
          <span className="header-title-main">League of Legends - Item Builder</span>
          <span className="header-title-sub">{t.titleSub}</span>
        </h1>
        <a
          className="language-switch"
          href={pathForLocale(locale === 'ko' ? 'en' : 'ko')}
          hrefLang={locale === 'ko' ? 'en' : 'ko'}
        >
          {t.languageSwitchLabel}
        </a>
        <div className="version-selector">
          <select
            value={version}
            onChange={(e) => handleVersionChange(e.target.value)}
            className="version-select"
            disabled={isLoading}
          >
            {availableVersions.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="content">
        <div className="left-panel">
          <div className="selected-items-container">
            <div className="selected-items slot-container">
              {selectedItems
                .map((id, index) => {
                  if (id === null) {
                    return (
                      <div key={index} className="empty-slot">
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className="selected-item"
                      onClick={() => (isTouch ? openItemSheet(id, index) : handleSelectedItemRemove(index))}
                      onMouseEnter={(e) => handleItemMouseEnter(id, e)}
                      onMouseLeave={handleItemMouseLeave}
                    >
                      <img
                        src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${id}.png`}
                        alt={`Item ${id}`}
                      />
                      {hoveredItem === id && overlayAnchor && (
                        <Overlay anchor={overlayAnchor}>
                          {renderOverlayContent(id)}
                        </Overlay>
                      )}
                    </div>
                  );
                })
                .sort((a, b) => {
                  if (a.props.className === 'empty-slot') return 1;
                  if (b.props.className === 'empty-slot') return -1;

                  const itemA = items.find(([itemId]) => itemId === a.key);
                  const itemB = items.find(([itemId]) => itemId === b.key);

                  if (!itemA || !itemB) return 0;

                  return itemB[1].gold.total - itemA[1].gold.total;
                })}
            </div>
            <button onClick={handleResetItems} className="reset-button">{t.reset}</button>
            <div className="total-gold">
              <span className="total-gold-label">{t.totalGoldLabel}</span>
              <strong>{calculateTotalGold()}</strong>
            </div>
          </div>
          <details
            className="summary-section total-stats"
            open={statsOpen}
            onToggle={(e) => setStatsOpen(e.currentTarget.open)}
          >
            <summary>{t.totalStats}</summary>
            {selectedItems.every(item => item === null) && (
              <p className="empty-slots-message">
                {t.emptySlots}
              </p>
            )}
            <ul className="total-stats-list">
              {Object.entries(totalStats).map(([stat, value]) => (
                <li key={stat} className="total-stat-item">
                  <span className="stat-icon">📊</span> {stat}: &nbsp;<strong>{value}</strong>
                </li>
              ))}
            </ul>
          </details>
          <details
            className="summary-section jack-of-all-trades"
            open={jackOpen}
            onToggle={(e) => setJackOpen(e.currentTarget.open)}
          >
            <summary>
              {t.jackOfAllTrades}
              <span className="summary-badge">{t.stacks(stackCount)}</span>
            </summary>
            <p>
              {t.currentStacks}<strong>{stackCount}</strong> ({t.abilityHaste}<strong>{abilityHaste}</strong>, {t.adaptiveBonus}<strong>{adaptiveBonus}</strong>)
            </p>
            <div className="jack-container">
              <img src={`${process.env.PUBLIC_URL}/jack.webp`} alt="" className="jack-image" />
              <div className="jack-description">
                <p style={{ fontSize: '0.75em' }}>
                  {t.jackDescription}
                </p>
              </div>
            </div>
            <div>
              <strong>{t.activeStats}</strong>
              <ul className="active-stats-list">
                {activeStats.map(stat => (
                  <li key={stat} className="active-stat-item">
                    <span className="stat-icon">✔️</span> {statName(stat)}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <strong>{t.inactiveStats}</strong>
              <ul className="inactive-stats-list">
                {inactiveStats.map(stat => (
                  <li key={stat} className="inactive-stat-item">
                    <span className="stat-icon">❌</span> {statName(stat)}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>
        <div className="right-panel">
          <div>
            <div className="search-container">
              <input
                type="text"
                id="search"
                name="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t.searchPlaceholder}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="filter-group">
            <h3>{t.categoryFilter}<span className="filter-label-suffix">{t.categoryFilterSuffix}</span></h3>
            <div className="filter-options">
              {Object.keys(categoryTranslation).map((tag) => (
                <button
                  key={tag}
                  className={`filter-button ${activeFilter.type === 'category' && activeFilter.value === tag ? 'active' : ''}`}
                  onClick={() => handleCategoryButtonClick(tag)}
                  disabled={isLoading}
                >
                  {categoryTranslation[tag]}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <h3>{t.jackFilter}<span className="filter-label-suffix">{t.jackFilterSuffix}</span></h3>
            <div className="filter-options">
              {jackOfAllTradesStats.map((stat) => (
                <button
                  key={stat}
                  className={`filter-button ${activeFilter.type === 'jack' && activeFilter.value === stat ? 'active' : ''}`}
                  onClick={() => handleJackOfAllTradesFilterClick(stat)}
                  disabled={isLoading}
                >
                  {statName(stat)}
                </button>
              ))}
            </div>
          </div>
          <div className="item-list" ref={itemListRef}>
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-indicator"></div>
                <p className="loading-text">{t.loading}</p>
              </div>
            ) : (
              filterItems(
                filterItemsBySearch(items, searchQuery)
              ).map(([id, item]) => (
                <div
                  key={id}
                  className="item"
                  onClick={() => (isTouch ? openItemSheet(id, null) : handleItemClick(id))}
                  onMouseEnter={(e) => handleItemMouseEnter(id, e)}
                  onMouseLeave={handleItemMouseLeave}
                >
                  <img
                    src={`https://ddragon.leagueoflegends.com/cdn/${version}/img/item/${id}.png`}
                    alt={item.name}
                  />
                  {hoveredItem === id && overlayAnchor && (
                    <Overlay anchor={overlayAnchor}>
                      {renderOverlayContent(id)}
                    </Overlay>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {sheetItem && (
        <ItemSheet
          actionLabel={
            sheetItem.slotIndex !== null
              ? t.sell
              : selectedItems.every((item) => item !== null)
                ? t.inventoryFull
                : t.buy
          }
          actionDisabled={sheetItem.slotIndex === null && selectedItems.every((item) => item !== null)}
          onAction={handleSheetAction}
          onClose={closeItemSheet}
        >
          {renderOverlayContent(sheetItem.id, true)}
        </ItemSheet>
      )}
      <footer className="footer">
        <p style={{ fontSize: '0.75em' }}>
          {t.disclaimer.map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </p>
      </footer>
    </div>
  );
};

export default App;